import {
  type BranchWithStats,
  type CreateBranchInput,
  type Page as PageDto,
  PERMISSIONS,
  type UpdateBranchInput,
} from '@edms/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Pencil, Plus, Search } from 'lucide-react';
import { useState } from 'react';

import { PageBody, PageHeader } from '@/components/page-header';
import { type Column, DataTable, Pagination } from '@/components/ui/data-table';
import { Dialog, useToast } from '@/components/ui/overlays';
import { Button, Checkbox, Field, Input } from '@/components/ui/primitives';
import { Badge, EmptyState } from '@/components/ui/surfaces';
import { useAuth } from '@/features/auth/auth-context';
import { api, ApiError } from '@/lib/api-client';
import { formatNumber, formatRelative } from '@/lib/utils';

/**
 * Gestion des bureaux (§9).
 *
 * La suppression n'existe pas : un bureau se désactive. Les archives qui le
 * référencent restent valides pendant toute leur durée de conservation, et
 * l'interface l'explique plutôt que de proposer une action que le serveur
 * refuserait.
 */
export function BranchesPage(): React.JSX.Element {
  const { can } = useAuth();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<BranchWithStats | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['branches', { search, page }],
    queryFn: () =>
      api.get<PageDto<BranchWithStats>>('/branches', {
        page,
        pageSize: 25,
        search: search || undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const writable = can(PERMISSIONS.BRANCHES_WRITE);

  const columns: readonly Column<BranchWithStats>[] = [
    {
      key: 'code',
      header: 'Code',
      width: '10%',
      render: (row) => <span className="font-mono text-sm font-medium text-ink">{row.code}</span>,
    },
    {
      key: 'name',
      header: 'Bureau',
      render: (row) => (
        <div className="flex flex-col">
          <span className="text-sm text-ink">{row.name}</span>
          {row.city && <span className="text-2xs text-ink-faint">{row.city}</span>}
        </div>
      ),
    },
    {
      key: 'users',
      header: 'Utilisateurs',
      align: 'right',
      width: '11%',
      render: (row) => <span className="text-sm text-ink-muted">{formatNumber(row.userCount)}</span>,
    },
    {
      key: 'receipts',
      header: 'Reçus archivés',
      align: 'right',
      width: '13%',
      render: (row) => <span className="text-sm text-ink">{formatNumber(row.receiptCount)}</span>,
    },
    {
      key: 'lastArchived',
      header: 'Dernier archivage',
      align: 'right',
      width: '14%',
      hideOnNarrow: true,
      render: (row) => (
        <span className="text-xs text-ink-faint">{formatRelative(row.lastArchivedAt)}</span>
      ),
    },
    {
      key: 'state',
      header: 'État',
      align: 'center',
      width: '10%',
      render: (row) => (
        <Badge tone={row.isActive ? 'success' : 'neutral'} dot>
          {row.isActive ? 'Actif' : 'Désactivé'}
        </Badge>
      ),
    },
    ...(writable
      ? [
          {
            key: 'actions',
            header: '',
            align: 'right' as const,
            width: '6%',
            render: (row: BranchWithStats) => (
              <Button size="sm" variant="ghost" onClick={() => setEditing(row)} title="Modifier">
                <Pencil className="size-3.5" />
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <PageHeader
        title="Bureaux"
        description="Chaque reçu et chaque utilisateur appartient à un seul bureau"
        actions={
          writable && (
            <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
              Nouveau bureau
            </Button>
          )
        }
        toolbar={
          <Input
            size="sm"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Rechercher un bureau…"
            leadingIcon={<Search className="size-4" />}
            className="max-w-sm"
            aria-label="Rechercher un bureau"
          />
        }
      />

      <PageBody>
        <div className="overflow-hidden rounded-lg border border-line-subtle bg-raised shadow-[var(--shadow-card)]">
          <DataTable
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(row) => row.id}
            loading={isLoading}
            empty={<EmptyState icon={Building2} title="Aucun bureau" />}
          />
          {data && (
            <Pagination
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              totalPages={data.totalPages}
              onPageChange={setPage}
            />
          )}
        </div>
      </PageBody>

      {creating && <BranchDialog onClose={() => setCreating(false)} />}
      {editing && <BranchDialog branch={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function BranchDialog({
  branch,
  onClose,
}: {
  branch?: BranchWithStats;
  onClose: () => void;
}): React.JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [code, setCode] = useState(branch?.code ?? '');
  const [name, setName] = useState(branch?.name ?? '');
  const [city, setCity] = useState(branch?.city ?? '');
  const [address, setAddress] = useState(branch?.address ?? '');
  const [phone, setPhone] = useState(branch?.phone ?? '');
  const [timezone, setTimezone] = useState(branch?.timezone ?? 'Africa/Kinshasa');
  const [isActive, setIsActive] = useState(branch?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        code: code.trim().toUpperCase(),
        name: name.trim(),
        city: city.trim() || undefined,
        address: address.trim() || undefined,
        phone: phone.trim() || undefined,
        timezone,
      };

      return branch
        ? api.patch(`/branches/${branch.id}`, { ...payload, isActive } satisfies UpdateBranchInput)
        : api.post('/branches', payload satisfies CreateBranchInput);
    },
    onSuccess: () => {
      toast.success(branch ? 'Bureau modifié' : 'Bureau créé');
      void queryClient.invalidateQueries({ queryKey: ['branches'] });
      onClose();
    },
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : 'Enregistrement impossible.'),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={branch ? `Modifier ${branch.name}` : 'Nouveau bureau'}
      footer={
        <>
          <Button onClick={onClose}>Annuler</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            {branch ? 'Enregistrer' : 'Créer'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <p role="alert" className="rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-xs text-danger">
            {error}
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Code"
            htmlFor="branch-code"
            required
            hint="2 à 12 caractères. Sert de préfixe aux références de lot."
          >
            <Input
              id="branch-code"
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              placeholder="KIN-01"
              className="font-mono"
              maxLength={12}
            />
          </Field>

          <Field label="Nom" htmlFor="branch-name" required>
            <Input id="branch-name" value={name} onChange={(event) => setName(event.target.value)} />
          </Field>

          <Field label="Ville" htmlFor="branch-city">
            <Input id="branch-city" value={city} onChange={(event) => setCity(event.target.value)} />
          </Field>

          <Field label="Téléphone" htmlFor="branch-phone">
            <Input id="branch-phone" value={phone} onChange={(event) => setPhone(event.target.value)} />
          </Field>

          <Field label="Adresse" htmlFor="branch-address" className="sm:col-span-2">
            <Input
              id="branch-address"
              value={address}
              onChange={(event) => setAddress(event.target.value)}
            />
          </Field>

          <Field label="Fuseau horaire" htmlFor="branch-timezone" className="sm:col-span-2">
            <Input
              id="branch-timezone"
              value={timezone}
              onChange={(event) => setTimezone(event.target.value)}
              className="font-mono"
            />
          </Field>
        </div>

        {branch && (
          <Checkbox
            label="Bureau actif"
            hint="Un bureau désactivé n'accepte plus de nouveaux lots. Ses archives restent consultables."
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
          />
        )}
      </div>
    </Dialog>
  );
}
